// src/utils/notifications.ts
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';

export type NotificationPriority = 'urgent' | 'high' | 'medium' | 'low';

export type NotificationType = 
  | BookingNotification
  | HorseNotification
  | FacilityNotification
  | SystemNotification
  | WaitlistNotification
  | BookingPendingConfirmationNotification; // Neuer Typ hinzugefügt

interface BaseNotification {
  id?: string; // ID wird automatisch von Firestore generiert
  createdAt: Date;
  recipientId: string;
  priority: NotificationPriority;
}

export interface BookingNotification extends BaseNotification {
  type: 'BOOKING_CREATED' | 'BOOKING_CONFIRMED' | 'BOOKING_CANCELLED' | 'BOOKING_REMINDER';
  bookingId: string;
  facilityName: string;
  horseName: string;
  startTime: Date;
}

export interface BookingPendingConfirmationNotification extends BaseNotification { // Neuer Interface
  type: 'BOOKING_PENDING_CONFIRMATION';
  bookingId: string;
  facilityName: string;
  horseName: string;
  startTime: Date;
}

export interface HorseNotification extends BaseNotification {
  type: 'HORSE_CREATED' | 'HORSE_UPDATE' | 'HORSE_DELETED' | 'HORSE_CORIDER_REQUEST' | 'HORSE_CORIDER_UPDATE' | 'HORSE_VET_REMINDER' | 'HORSE_FARRIER_REMINDER';
  horseId?: string;
  horseName: string;
  details?: {
    requesterId?: string;
    requesterName?: string;
    appointmentDate?: Date;
    status?: 'approved' | 'denied';
  };
}

export interface FacilityNotification extends BaseNotification {
  type: 'FACILITY_MAINTENANCE' | 'FACILITY_AVAILABLE' | 'FACILITY_BLOCKED';
  facilityId: string;
  facilityName: string;
  details?: {
    startDate?: Date;
    endDate?: Date;
    reason?: string;
  };
}

export interface SystemNotification extends BaseNotification {
  type: 'SYSTEM_ANNOUNCEMENT' | 'FEATURE_UPDATE' | 'MAINTENANCE_MODE';
  title: string;
  message: string;
  action?: {
    label: string;
    url: string;
  };
}

// Neuer Typ für Wartelisten-Einträge
export interface WaitlistNotification extends BaseNotification {
  type: 'WAITLIST_ENTRY';
  facilityName: string;
  horseName: string;
  startTime: Date;
}

/**
 * Funktion zum Erstellen einer Benachrichtigung in Firestore
 * @param notification - Die Benachrichtigungsdaten, die gespeichert werden sollen
 */
export const createNotification = async (notification: NotificationType): Promise<void> => {
  try {
    if (!notification.recipientId) {
      throw new Error('recipientId ist erforderlich, aber nicht definiert.');
    }

    console.log('Erstelle Benachrichtigung:', notification);

    await addDoc(collection(db, 'notifications'), {
      metadata: { // Einbetten der Benachrichtigungsdaten in das metadata-Feld
        ...notification,
      },
      createdAt: notification.createdAt || new Date(), // Sicherstellen, dass createdAt gesetzt ist
    });

    console.log('Benachrichtigung erfolgreich erstellt.');
  } catch (error) {
    console.error('Fehler beim Erstellen der Benachrichtigung:', error);
    throw new Error('Fehler beim Erstellen der Benachrichtigung');
  }
};
